import styled, { css, DefaultTheme } from 'styled-components';
import { Icon } from 'src/components';
import { ResponseState } from 'src/apps/LearningSession/hooks/learningSessionReducer';

export const Container = styled.div<{ $onVideo?: boolean }>(
  ({ theme, $onVideo }) => css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    // clip rather than hidden, so the vertical axis stays visible
    // and the backspace button's shadow isn't cut off
    overflow-x: clip;

    ${
      $onVideo &&
      css`
        box-sizing: border-box;
        min-height: 0;
        padding: ${theme.sz.s4};
        border-radius: ${theme.bo.medium};
        background: linear-gradient(
          160deg,
          rgba(0, 0, 0, 0.62) 0%,
          rgba(0, 0, 0, 0.42) 55%,
          rgba(0, 0, 0, 0.28) 100%
        );
      `
    }
  `,
);

export const BottomContainer = styled.div<{ $onVideo?: boolean }>(
  ({ theme, $onVideo }) => css`
    display: flex;
    width: 100%;
    min-height: 0;
    padding: 0 ${theme.sz.s1};
    justify-content: center;

    @media (max-width: ${theme.sz.lg}) {
      /* On video the height is capped, so wrapping would push the hint
         buttons into an off-screen column instead of scrolling the keys. */
      flex-wrap: ${$onVideo ? 'nowrap' : 'wrap'};
      flex-direction: column-reverse;
    }
  `,
);

export const KeyContainer = styled.div<{ $onVideo?: boolean }>(
  ({ theme, $onVideo }) => css`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    padding: 0 0 ${theme.sz.s1};
    margin-top: -${theme.sz.s4};
    margin-inline-start: -${theme.sz.s2};
    margin-inline-end: -${theme.sz.s2};

    & > * {
      margin-top: ${theme.sz.s4};
      margin-inline-end: ${theme.sz.s2};
      margin-inline-start: ${theme.sz.s2};
    }

    ${
      $onVideo &&
      css`
        min-height: 0;
        align-content: flex-start;
        overflow-y: auto;
        overscroll-behavior: contain;
        padding-top: ${theme.sz.s1};
        margin-top: -${theme.sz.s3};

        & > * {
          margin-top: ${theme.sz.s3};
        }

        @media (max-width: ${theme.sz.md}) {
          margin-top: -${theme.sz.s2};
          margin-inline-start: -${theme.sz.s1};
          margin-inline-end: -${theme.sz.s1};

          & > * {
            margin-top: ${theme.sz.s2};
            margin-inline-end: ${theme.sz.s1};
            margin-inline-start: ${theme.sz.s1};
          }
        }
      `
    }
  `,
);

export const HintContainer = styled.div<{ $onVideo?: boolean }>(
  ({ theme, $onVideo }) => css`
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: ${theme.sz.s4};
    margin-left: ${theme.sz.s8};

    @media (max-width: ${theme.sz.lg}) {
      margin-left: 0;
      margin-bottom: ${theme.sz.s8};
    }

    ${
      $onVideo &&
      css`
        @media (max-width: ${theme.sz.md}) {
          flex-direction: row;
          justify-content: space-between;
          gap: ${theme.sz.s2};
          margin-bottom: ${theme.sz.s4};
        }
      `
    }
  `,
);

const getInputColours = (
  responseState: ResponseState,
  theme: DefaultTheme,
): {
  color: string;
  background: string;
  border: string;
} => {
  if (responseState === 'Correct') {
    return {
      border: theme.co.success40,
      background: theme.co.success80,
      color: theme.co.success0,
    };
  }
  if (responseState === 'Incorrect') {
    return {
      border: theme.co.error40,
      background: theme.co.error80,
      color: theme.co.error20,
    };
  }
  if (responseState === 'NearlyCorrect') {
    return {
      border: theme.co.warning20,
      background: theme.co.warning80,
      color: theme.co.warning0,
    };
  }
  return {
    border: theme.co.secondary80,
    background: theme.co.neutral95,
    color: theme.co.secondary20,
  };
};

export const Input = styled.input<{
  $responseState: ResponseState;
  $fontSize: string;
  $isRtlTarget: boolean;
}>(
  ({ theme, $responseState, $fontSize, $isRtlTarget }) => css`
    width: 100%;
    height: ${theme.sz.s16};
    font-size: ${$fontSize};
    padding: 0 ${theme.sz.s4};
    border-radius: ${theme.sz.s3};
    border: 2px solid ${getInputColours($responseState, theme).border};
    background-color: ${getInputColours($responseState, theme).background};
    color: ${getInputColours($responseState, theme).color};

    &:placeholder-shown {
      /*! @noflip */
      direction: ${$isRtlTarget ? 'rtl' : 'ltr'};
    }
  `,
);

export const InputWrapper = styled.div<{
  $placeholderText: string;
  $fontSize: string;
}>(
  ({ theme, $placeholderText, $fontSize }) => css`
    position: relative;
    width: 100%;
    margin: 0 ${theme.sz.s1};

    &::after {
      pointer-events: none;
      display: block;
      width: 100%;
      height: ${theme.sz.s16};
      line-height: ${theme.sz.s16};
      content: '${$placeholderText}';
      font-size: ${$fontSize};
      padding: 0 ${theme.sz.s4};
      margin: 0 2px;
      position: absolute;
      top: 0;
      opacity: 0.5;
    }
  `,
);

export const Label = styled.div<{ $onVideo?: boolean }>(
  ({ theme, $onVideo }) => css`
    flex-shrink: 0;
    text-transform: uppercase;
    margin-bottom: ${theme.sz.s4};
    font-size: ${theme.DEPRECATED_ty.small};
    font-weight: ${theme.DEPRECATED_ty.semiBold};
    ${
      $onVideo &&
      css`
        color: ${theme.co.neutral99};
      `
    }
  `,
);

export const InputContainer = styled.div<{ $onVideo?: boolean }>(
  ({ theme, $onVideo }) => css`
    position: relative;
    flex-shrink: 0;
    width: 100%;
    max-width: unset;
    display: flex;
    flex-direction: row;
    margin-bottom: ${theme.sz.s8};

    ${
      $onVideo &&
      css`
        @media (max-width: ${theme.sz.md}) {
          margin-bottom: ${theme.sz.s4};
        }
      `
    }
  `,
);

export const HintIcon = styled(Icon)(
  ({ theme }) => css`
    margin-right: ${theme.sz.s4};
    margin-top: -${theme.sz.s2}; // to offset icon viewport
  `,
);

export const HintButton = styled.button<{ $showBigger?: boolean; $onVideo?: boolean }>(
  ({ theme, $showBigger, $onVideo }) => css`
    height: ${$showBigger ? theme.sz.s16 : theme.sz.s12};
    font-size: ${$showBigger ? theme.DEPRECATED_ty.large : theme.DEPRECATED_ty.mediumLarge};
    padding: ${$showBigger ? `0 ${theme.sz.s6}` : `0 ${theme.sz.s4}`};

    display: flex;
    justify-content: center;
    align-items: center;

    color: ${theme.co.secondary20};
    background: ${$onVideo ? theme.co.neutral99 : 'transparent'};

    cursor: pointer;
    border: ${theme.bo.mediumWidth} solid ${theme.co.secondary80};
    box-shadow: 0 ${theme.sz.s1} 0 ${theme.co.secondary80};
    border-radius: ${theme.bo.medium};

    font-weight: ${theme.DEPRECATED_ty.bold};

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
      box-shadow: 0 ${theme.sz.s1} 0 ${theme.co.secondary80};
    }

    &:not(:disabled):active,
    &:not(:disabled):active:visited,
    &:not(:disabled):hover,
    &:not(:disabled):hover:visited {
      box-shadow: 0 ${theme.bo.veryThickWidth} 0 ${theme.co.secondary80};
      transform: translateY(-2px);
    }
  `,
);

export const BackspaceButton = styled(HintButton)`
  aspect-ratio: 1;
  padding: 0;
`;

export const BackspaceIcon = styled(Icon)<{ $showBigger?: boolean }>(
  ({ theme, $showBigger }) => css`
    font-size: ${$showBigger ? theme.sz.s10 : theme.sz.s8};
  `,
);
